// Il team del progetto: i pops con il loro modello e la loro fase. «Aggiungi pop» li prende dal
// catalogo dei Pops; «Modifica» apre nome, avatar, modello, fase e istruzioni del membro.
import { useEffect, useRef, useState } from 'react';
import { Button } from '../../../components/Button';
import { Field, TextArea, TextField } from '../../../components/Field';
import { Pop } from '../../../components/Pop';
import { Select } from '../../../components/Select';
import type { AgentProfile, ConnectedProvider, PlanMember } from '../../../lib/api';
import { popRole } from '../../../lib/pops';
import { ModelPair } from '../../agents/ModelPair';
import { PopPicker } from '../../agents/PopPicker';
import { loadPopNames, memberOf } from './plan';

function AddMenu({
  profiles,
  onPick,
}: {
  profiles: AgentProfile[];
  onPick: (p: AgentProfile) => void;
}) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);
  return (
    <div ref={box} style={{ position: 'relative' }}>
      <Button
        variant="primary"
        size="sm"
        icon="plus"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        Aggiungi pop
      </Button>
      {open && (
        <div role="menu" className="np-menu">
          <div className="t-overline faint" style={{ padding: '6px 8px' }}>
            Dal catalogo dei Pops
          </div>
          {profiles.map((p) => (
            <button
              key={p.id}
              type="button"
              role="menuitem"
              className="np-menu-item"
              onClick={() => {
                onPick(p);
                setOpen(false);
              }}
            >
              <Pop avatar={p.avatar} size={34} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span className="t-strong" style={{ display: 'block' }}>
                  {p.label}
                </span>
                <span className="t-caption dim trunc" style={{ display: 'block' }}>
                  {p.duty}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function TeamEditor({
  team,
  onChange,
  profiles,
  providers,
  phases,
}: {
  team: PlanMember[];
  onChange: (team: PlanMember[]) => void;
  profiles: AgentProfile[];
  providers: ConnectedProvider[];
  phases: string[];
}) {
  const [editing, setEditing] = useState<string | null>(null);
  useEffect(() => {
    void loadPopNames();
  }, []);
  const label = (m: PlanMember) => profiles.find((p) => p.id === m.profile)?.label ?? m.profile;
  const put = (i: number, patch: Partial<PlanMember>) =>
    onChange(team.map((m, j) => (j === i ? { ...m, ...patch } : m)));

  return (
    <section className="np-card">
      <header className="np-card-head">
        <h2 className="t-overline">Team</h2>
        <span className="t-caption faint">{team.length} pops</span>
        <span style={{ marginLeft: 'auto' }}>
          <AddMenu
            profiles={profiles.filter((p) => !p.coordinator)}
            onPick={(p) => onChange([...team, memberOf(p, team)])}
          />
        </span>
      </header>
      {team.length === 0 && (
        <div className="t-caption faint">Nessun pop ancora: aggiungili dal catalogo.</div>
      )}
      {team.map((m, i) => (
        <div key={i} className="np-member">
          <div className="np-member-row">
            {m.avatar && <Pop avatar={m.avatar} size={48} label={m.name} />}
            <span style={{ flex: 1, minWidth: 0 }}>
              <span className="t-strong" style={{ display: 'block' }}>
                {m.name}
              </span>
              <span className="t-caption dim">{label(m)}</span>
            </span>
            <span className="np-member-model">
              <ModelPair
                providers={providers}
                who={`di ${m.name}`}
                value={m.model}
                onChange={(model) => put(i, { model })}
                followsFrom="dal pop"
                followsName="Quello del pop"
              />
            </span>
            {m.phase && <span className="dash-pill np-phase-pill">{m.phase}</span>}
            <Button size="sm" onClick={() => setEditing(editing === m.name ? null : m.name)}>
              {editing === m.name ? 'Chiudi' : 'Modifica'}
            </Button>
            <Button
              variant="icon"
              icon="x"
              aria-label={`Togli ${m.name}`}
              onClick={() => onChange(team.filter((_, j) => j !== i))}
            />
          </div>
          {editing === m.name && (
            <div className="np-member-edit">
              <div className="np-two">
                <Field label="Nome">
                  <TextField value={m.name} onChange={(v) => put(i, { name: v })} />
                </Field>
                <Field label="Fase">
                  <Select
                    label="Fase"
                    value={m.phase}
                    onChange={(v) => put(i, { phase: v })}
                    options={[
                      { value: '', label: 'Tutte' },
                      ...phases.map((p) => ({ value: p, label: p })),
                    ]}
                    width="100%"
                  />
                </Field>
              </div>
              <Field label="Avatar">
                <PopPicker
                  role={m.avatar ? popRole(m.avatar) : null}
                  value={m.avatar}
                  onChange={(avatar) => put(i, { avatar })}
                />
              </Field>
              <Field label="Istruzioni in più per questo progetto">
                <TextArea
                  value={m.instructions}
                  onChange={(v) => put(i, { instructions: v })}
                  rows={3}
                />
              </Field>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  icon="check"
                  onClick={() => {
                    if (!m.name.trim()) return;
                    setEditing(null);
                  }}
                >
                  Fatto
                </Button>
              </div>
            </div>
          )}
        </div>
      ))}
      {team.some((m, i) => team.findIndex((x) => x.name === m.name) !== i) && (
        <div className="t-caption" style={{ color: 'var(--danger)' }}>
          Due pops hanno lo stesso nome: cambiane uno.
        </div>
      )}
    </section>
  );
}
